// Left navigation — TWO panes as of 2026-08-23 (Grant's design call).
//
//   ┌────┬──────────────┬──────────────────
//   │ 56 │  220px pane  │  page content
//   │ px │  (optional)  │
//   │icon│  the open    │
//   │rail│  section's   │
//   │    │  destinations│
//
// The rail is a fixed column of heading icons and never moves. Clicking
// one opens the second pane beside it with that heading's destinations;
// clicking the same icon again shuts it.
//
// Why two panes and not an accordion (Grant, 2026-08-23: "what if there
// was a second pane in the left that opened to the right of it? This
// would keep the option in the left hand pane static"):
//
//   1. Nothing reflows. With an accordion, opening EMAIL shoved Tasks,
//      Calendar, Automations and Forms down the screen — the list jumped
//      under the cursor on every click.
//   2. Email and Admin stop crowding everything else out. The live mail
//      folder tree is tall and ADMIN carries ~15 rows across 7
//      subgroups; in an accordion either one pushed the rest off-screen.
//      Given a pane of their own, each gets the full height.
//   3. It collapsed the OLD two-states problem. "Rail collapsed" and
//      "sections collapsed" were separate flags that could disagree, and
//      collapsing the rail rendered every individual item as an
//      unlabeled icon — noisier than the expanded rail, not simpler.
//      Now one value (ChromeContext.activeNavSection) describes it all,
//      and the collapsed state is genuinely a table of contents.
//
// Defaults: the pane starts CLOSED for everyone; Settings → Preferences
// → Navigation switches it to "open on the section I used last".
//
// Section content lives in sidebar-data.ts as a pure module so the
// composition + role gating unit-test without a DOM. Pinned pages render
// as icons at the foot of the rail, so the handful of deep screens a
// person uses daily stay one click away no matter what is open.

import {
  Activity,
  Archive,
  BadgeCheck,
  Banknote,
  BookOpen,
  Bookmark,
  Briefcase,
  Building,
  Building2,
  Calculator,
  Calendar,
  CalendarCheck,
  CalendarCog,
  ChartColumn,
  ChartLine,
  CircleCheck,
  CircleDot,
  ClipboardList,
  Clock,
  Columns3,
  Compass,
  Cpu,
  Database,
  Eye,
  FileChartColumn,
  FilePen,
  FileText,
  Folder,
  FolderCheck,
  Gauge,
  GraduationCap,
  HandCoins,
  Handshake,
  HeartHandshake,
  House,
  IdCard,
  Inbox,
  Key,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  Library,
  LifeBuoy,
  Link2,
  List,
  ListChecks,
  ListFilter,
  Lock,
  Mail,
  MailCheck,
  MailOpen,
  Mails,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Mic,
  Network,
  NotebookPen,
  Package,
  Pen,
  PenTool,
  Percent,
  Plug,
  RadioTower,
  Receipt,
  Route,
  ScanSearch,
  Send,
  Settings,
  Settings2,
  Share2,
  Shield,
  ShieldAlert,
  Star,
  ShieldCheck,
  SlidersHorizontal,
  SquareCheckBig,
  SquareKanban,
  Sun,
  Target,
  Trash2,
  TrendingUp,
  Type,
  Umbrella,
  Upload,
  User,
  Users,
  Wallet,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { api, ApiError, type SessionUser } from "../api";
import { useChrome } from "./ChromeContext";
import { CoreVocabularyTip } from "./CoreVocabularyTip";
import { EmailSidebarSection } from "./email/EmailSidebarSection";
import {
  buildMobileClientsSection,
  buildSidebarSections,
  sectionForPath,
  sidebarItemIsActive,
  withDataNeededBadge,
  type IconName,
  type SidebarItem,
  type SidebarSection,
} from "./sidebar-data";
import { isPinned, useNavPins } from "./nav-pins";
import { useUserSetting } from "../settings/user-settings-context";
import { NAV_ADMIN_GROUPS_MODE, NAV_COLLAPSED_SECTIONS } from "../settings/user-settings-registry";

// The "this section is closed" set is keyed on heading text — headings
// are stable enough (short uppercase labels like "ADMIN" / "COMPANIES")
// to serve as keys. Absent from the set = open. Storage moved to the
// account in 2026-08-15; see the NAV_COLLAPSED_SECTIONS descriptor.

interface SidebarProps {
  user: SessionUser;
}


// B7 (2026-07-23) — the Data Needed badge counts what its label has
// always promised: the caller's judgment calls PLUS the cleanup items in
// their lane (judgment_open + my_lane_open — the same two disjoint sets
// the /data-needed page shows). It previously counted judgment only,
// which read absurdly low (Grant saw "2" against a 777-item lane).
// Served by /api/data-needed/counts since the sprint board that used to
// host this pair was removed at cutover (2026-07-31).
// 2026-09-15 — two fixes, both from one screenshot where this badge read
// 746 against a page reading 983.
//
// AGE was the whole 237-item gap. The effect below used to depend on
// `[user]` alone, and the session user never changes, so the badge
// fetched once when the tab was opened and then showed that number for
// the rest of the day while the page refetched on every filter change.
// It now also depends on the current path, so every navigation refreshes
// it — no timer, no polling, and it cannot go stale while someone is
// actually using the OS. (A number nobody navigates past is a number
// nobody is reading.)
//
// And the sum moved to the server: render `total`, which the API added,
// rather than adding the two halves here. The page renders the same
// field from the same endpoint, so the two can no longer hold different
// arithmetic.
function useDataNeededBadge(user: SessionUser, pathname: string): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await api.getDataNeededCounts();
        if (cancelled) return;
        // A number or nothing — never undefined. See the same guard on
        // DataNeededPage: a non-number slipped past every null check and
        // blanked the render.
        setCount(typeof p.total === "number" && Number.isFinite(p.total) ? p.total : null);
      } catch (err) {
        if (!(err instanceof ApiError)) {
          console.error("data needed badge fetch failed", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, pathname]);

  return count;
}

export function Sidebar({ user }: SidebarProps) {
  const chrome = useChrome();
  const location = useLocation();
  const sidebarId = useId();
  const dataNeededCount = useDataNeededBadge(user, location.pathname);
  const sections = withDataNeededBadge(buildSidebarSections(user), dataNeededCount);

  // Per-user pins (nav-redesign PR 6) — follow the ACCOUNT as of
  // 2026-08-15, so the screens you pinned are pinned on every machine.
  // They are NOT a section: another growing heading is exactly what the
  // rail's fixed-heading design exists to avoid. They render as icons at
  // the foot of the rail instead, which is what makes "start collapsed" cheap —
  // the deep screens you open daily stay one click away with every pane
  // shut.
  const { pins, toggle: togglePinned } = useNavPins();
  const handleTogglePin = useCallback(
    (item: SidebarItem) => {
      togglePinned({ label: item.label, to: item.to, icon: item.icon });
    },
    [togglePinned],
  );

  // Which groups are folded — Admin's People / Data / Compliance / … on
  // desktop, and the section headings in the mobile drawer. Keyed by
  // "SECTION::Group" (or "mobile::HEADING") and following the account.
  const [remembered, setRemembered] = useUserSetting(NAV_COLLAPSED_SECTIONS);
  const [groupsMode] = useUserSetting(NAV_ADMIN_GROUPS_MODE);

  // In "remember" mode a fold is a preference and persists to the
  // account. In "collapsed"/"open" it is a per-visit convenience: the
  // setting decides what you see on arrival, and opening a group while
  // you work does not quietly rewrite your default. That distinction is
  // the whole point of offering the choice — without it, "start
  // collapsed" would last exactly until the first time you opened one.
  const [session, setSession] = useState<Set<string>>(() => new Set());
  const persistFolds = groupsMode === "remember";
  const collapsedSections = persistFolds ? remembered : session;

  const toggleSubgroup = useCallback(
    (key: string) => {
      const apply = (prev: Set<string>) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      };
      if (persistFolds) setRemembered(apply(remembered));
      else setSession(apply);
    },
    [persistFolds, remembered, setRemembered],
  );

  // Seed the per-visit set when the mode is not "remember". Runs when the
  // mode arrives from the account (it is "collapsed" while the settings
  // fetch is in flight, which is also the shipped default, so nothing
  // flashes) and whenever the user changes it in Settings.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (persistFolds || seededFor.current === groupsMode) return;
    seededFor.current = groupsMode;
    setSession(groupsMode === "collapsed" ? new Set(allGroupKeys(sections)) : new Set());
  }, [groupsMode, persistFolds, sections]);

  const sectionSlug = useCallback(
    (heading: string) =>
      heading
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, ""),
    [],
  );

  const { isMobileNavOpen, closeMobileNav, activeNavSection, toggleNavSection } = chrome;
  const showAllPages = user.employee_role_slug !== "ambassador";

  // The section whose pane is open, resolved against the sections this
  // user actually has. A stored key can name a section their role no
  // longer includes — "admin" after a role change, or "email" restored
  // for a back_office user who gets no Email section.
  const openSection = sections.find((sec) => sec.key === activeNavSection) ?? null;

  // Which section the CURRENT PAGE lives in, regardless of whether its
  // pane happens to be open. This is what lets a rail icon answer "am I
  // here?" even with every pane closed — the case the audit found
  // missing: the icons only ever lit up for the open pane, not for the
  // page you were actually looking at.
  const currentSection = sectionForPath(location.pathname, sections);

  // …and when it can't be resolved, tell ChromeContext, don't just render
  // around it. Rendering around it left the two disagreeing: the rail
  // LOOKED shut while the state said open, so ⌘/ closed something already
  // invisible and the first press did nothing. That is the same "the
  // button does nothing" complaint Nate raised about the old collapse
  // logic (2026-07-23) — a state that only the renderer knows is wrong.
  const { closeNavSection } = chrome;
  useEffect(() => {
    if (activeNavSection !== null && !openSection) closeNavSection();
  }, [activeNavSection, openSection, closeNavSection]);

  // Mobile drawer focus management: trap Tab inside the drawer, close on
  // Escape, restore focus to the TopNav hamburger on close. Desktop is
  // unaffected (isMobileNavOpen is only ever set by the mobile-only
  // hamburger).
  const asideRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!isMobileNavOpen) return;
    const aside = asideRef.current;
    if (!aside) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const selector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = () =>
      Array.from(aside.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => el.offsetParent !== null,
      );
    focusables()[0]?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMobileNav();
        return;
      }
      if (e.key !== "Tab") return;
      const f = focusables();
      if (f.length === 0) return;
      const first = f[0]!;
      const last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    // Audit PR 11 fix: close the drawer if the viewport grows past the
    // mobile breakpoint while it's open — otherwise the focus trap +
    // keydown interceptor would keep running on desktop where the drawer
    // is CSS-hidden.
    function onResize() {
      if (window.innerWidth > 1023) closeMobileNav();
    }
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
      previouslyFocused?.focus?.();
    };
  }, [isMobileNavOpen, closeMobileNav]);

  // On a phone the two-pane rail does not fit — there is no room for a
  // 68px strip AND a 220px pane AND the page — so the hamburger drawer
  // shows one labeled column instead.
  //
  // It keeps BOTH things the desktop rail gives you, because the first
  // pass dropped them and that was a straight regression on phones:
  //   * Pinned pages, at the top. They used to ride along because pins
  //     were a section in the shared list; splitting the rail out left
  //     them desktop-only, so anyone who pinned pages lost them here.
  //   * Foldable section headings. Every heading used to be a fold
  //     button. Without them an admin's drawer is ~40 links in one
  //     unbroken scroll, which is the phone version of the wall of icons
  //     this whole redesign set out to remove.
  if (isMobileNavOpen) {
    // The six primary record lanes live in the top menu bar on desktop —
    // hidden at phone width — so the drawer carries them here or nowhere
    // (see buildMobileClientsSection). Pins stay first; lanes come next.
    const clientsSection = buildMobileClientsSection(user);
    const drawerSections: SidebarSection[] =
      pins.length > 0
        ? [
            { key: "me", icon: "bookmark", heading: "PINNED", items: pins },
            clientsSection,
            ...sections,
          ]
        : [clientsSection, ...sections];
    return (
      <aside
        ref={asideRef}
        id={sidebarId}
        className="sidebar is-mobile-open"
        aria-label="Secondary"
        data-testid="sidebar"
        data-collapsed="false"
        data-mobile-open="true"
      >
        <nav className="sidebar-nav">
          {drawerSections.map((section, idx) => {
            // PINNED borrows the "me" key so the array stays typed; key
            // the React node and the fold state on the HEADING here, which
            // is unique within the drawer, so it can't collide with the
            // real "me" section below it.
            const heading = section.heading ?? section.key;
            const foldKey = `mobile::${heading}`;
            const isClosed = collapsedSections.has(foldKey);
            return (
              <div
                className={`sidebar-section is-collapsible${isClosed ? " is-closed" : ""}`}
                key={`${heading}-${idx}`}
              >
                <div className="sidebar-section-title">
                  <button
                    type="button"
                    className="sidebar-section-toggle"
                    aria-expanded={!isClosed}
                    aria-controls={`sb-mobile-${idx}`}
                    onClick={() => toggleSubgroup(foldKey)}
                    data-testid={`sidebar-section-toggle-${sectionSlug(heading)}`}
                  >
                    <span>{heading}</span>
                    <ChevronIcon />
                  </button>
                </div>
                <SectionBody
                  section={section}
                  sectionId={`sb-mobile-${idx}`}
                  pathname={location.pathname}
                  pins={pins}
                  onTogglePin={handleTogglePin}
                  collapsedSections={collapsedSections}
                  toggleSubgroup={toggleSubgroup}
                  sectionSlug={sectionSlug}
                />
              </div>
            );
          })}
          {showAllPages && (
            <ul className="sidebar-list sidebar-all-pages">
              <li>
                <SidebarLink
                  item={{ label: "All pages", to: "/all-pages", icon: "layout-grid" }}
                  pathname={location.pathname}
                  collapsed={false}
                />
              </li>
            </ul>
          )}
        </nav>
      </aside>
    );
  }

  return (
    <aside
      ref={asideRef}
      id={sidebarId}
      className={`sidebar${openSection ? "" : " is-collapsed"}`}
      aria-label="Secondary"
      data-testid="sidebar"
      data-collapsed={openSection ? "false" : "true"}
      data-mobile-open="false"
      data-open-section={openSection?.key ?? ""}
    >
      {/* ── The rail: heading icons, always visible ───────────────
          These are DISCLOSURE buttons (aria-expanded + aria-controls), not
          tabs. They were built with role="tab"/"tablist" and that was
          wrong twice over: the tab pattern promises arrow-key movement
          between tabs, which was never implemented, and a tablist may
          only contain tabs — while this rail also holds a divider and the
          pinned links. Show/hide is what this actually does, and plain
          buttons say so accurately with no extra keyboard contract. */}
      <div className="nav-rail">
        <ul className="nav-rail-sections" aria-label="Navigation sections">
          {sections.map((section) => {
            const isOpen = openSection?.key === section.key;
            // "You are here" — true whether or not the pane is open. See
            // the CSS: is-open gets a filled background + bar (you're
            // looking straight at this panel); is-current alone gets just
            // the bar (the page belongs here, but you closed the pane).
            const isCurrent = currentSection?.key === section.key;
            const label = railLabel(section.railLabel ?? section.heading ?? section.key);
            return (
              <li key={section.key}>
                <button
                  type="button"
                  className={`nav-rail-btn${isOpen ? " is-open" : ""}${isCurrent ? " is-current" : ""}`}
                  aria-expanded={isOpen}
                  aria-controls={isOpen ? `nav-pane-${section.key}` : undefined}
                  aria-current={isCurrent ? "page" : undefined}
                  // The icon is aria-hidden and the text label can be
                  // truncated, so name the button explicitly — otherwise a
                  // long first name reads to a screen reader as "Christoph…"
                  // (the same trap as audit P0-9 on the collapsed links).
                  // Title case, matching what is on screen, so voice
                  // control ("click Automations") hits the visible word.
                  aria-label={label}
                  title={label}
                  onClick={() => toggleNavSection(section.key)}
                  data-testid={`nav-rail-btn-${section.key}`}
                >
                  <span className="nav-rail-icon" aria-hidden="true">
                    <SidebarIcon name={section.icon} />
                  </span>
                  <span className="nav-rail-label" aria-hidden="true">
                    {label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Pinned pages — icons under a divider, so the screens someone
            opens every day stay one click away with every pane shut.
            This is what keeps "start collapsed" from costing clicks. */}
        {pins.length > 0 && (
          <>
            <div className="nav-rail-divider" role="presentation" />
            <ul className="nav-rail-pins" aria-label="Pinned pages">
              {pins.map((pin) => {
                const isActive = sidebarItemIsActive(location.pathname, pin.to);
                return (
                  <li key={pin.to}>
                    <Link
                      to={pin.to}
                      className={`nav-rail-btn nav-rail-pin${isActive ? " is-active" : ""}`}
                      // Full label, not the truncated one on screen.
                      aria-label={pin.label}
                      aria-current={isActive ? "page" : undefined}
                      title={pin.label}
                      data-testid={`nav-rail-pin-${pin.to}`}
                    >
                      <span className="nav-rail-icon" aria-hidden="true">
                        <SidebarIcon name={pin.icon} />
                      </span>
                      {/* Labeled for the same reason the headings above
                          are: an unlabeled glyph is a memory test, and a
                          pinned page's icon is arbitrary — several share
                          the same one. Leaving these bare while labeling
                          the headings above them was just inconsistent. */}
                      <span className="nav-rail-label" aria-hidden="true">
                        {railLabel(pin.label)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {/* All pages (2026-10-15): the directory of every page this person
            may open, pinned to the foot of the strip so "where is X?"
            always has a one-click answer. Not for the ambassador role,
            whose rail is deliberately just its two pages. */}
        {showAllPages && (
          <>
            <div className="nav-rail-spacer" role="presentation" />
            <Link
              to="/all-pages"
              className={`nav-rail-btn${location.pathname === "/all-pages" ? " is-active" : ""}`}
              aria-label="All pages"
              aria-current={location.pathname === "/all-pages" ? "page" : undefined}
              title="All pages"
              data-testid="nav-rail-all-pages"
            >
              <span className="nav-rail-icon" aria-hidden="true">
                <SidebarIcon name="layout-grid" />
              </span>
              <span className="nav-rail-label" aria-hidden="true">
                All pages
              </span>
            </Link>
          </>
        )}
      </div>

      {/* ── The second pane: the open section's destinations ────────── */}
      {openSection && (
        <div
          className="nav-pane"
          id={`nav-pane-${openSection.key}`}
          role="tabpanel"
          aria-label={openSection.heading ?? openSection.key}
        >
          <div className="nav-pane-header">
            <span className="nav-pane-title">{openSection.heading}</span>
            <button
              type="button"
              className="nav-pane-close"
              aria-label={`Close ${openSection.heading} (⌘ /)`}
              title="Close (⌘ /)"
              onClick={chrome.closeNavSection}
              data-testid="nav-pane-close"
            >
              <PanelIcon collapsed={false} />
            </button>
          </div>
          <nav className="sidebar-nav" aria-label={openSection.heading ?? undefined}>
            <SectionBody
              section={openSection}
              sectionId={`sb-section-${openSection.key}`}
              pathname={location.pathname}
              pins={pins}
              onTogglePin={handleTogglePin}
              collapsedSections={collapsedSections}
              toggleSubgroup={toggleSubgroup}
              sectionSlug={sectionSlug}
            />
          </nav>
        </div>
      )}
    </aside>
  );
}

// The group keys that start folded in the "collapsed" mode, matching
// exactly what SectionBody builds. Only groups marked startFolded: all of
// Admin's, and Company tools' company groups for people who serve all
// five companies. Marketing's three short groups and the Company tools
// "Every company" group stay open, so opening those panels shows rows,
// not a column of headings. The mobile drawer's own "mobile::HEADING"
// folds are deliberately NOT seeded: on a phone the drawer opens on
// demand and arriving with every heading shut would hide the whole menu
// behind one tap per heading.
function allGroupKeys(sections: SidebarSection[]): string[] {
  return sections.flatMap((section) =>
    (section.subgroups ?? [])
      .filter((g) => g.startFolded)
      .map((g) => `${section.heading ?? section.key}::${g.heading}`),
  );
}

// The two-line rail button carries a tiny text label under its icon.
// A column of unlabeled glyphs is a memory test — and the one heading
// that IS a person's own first name has no conventional icon at all, so
// that one would be pure guesswork.
//
// The rail is sized (68px) so the longest fixed heading, "Automations",
// fits whole. Only a long PERSON's name can overflow it, and those are
// trimmed rather than wrapped so every button stays the same height and
// none of them shift.
const RAIL_LABEL_MAX = 11;

function railLabel(label: string): string {
  const pretty = label.charAt(0) + label.slice(1).toLowerCase();
  return pretty.length > RAIL_LABEL_MAX ? `${pretty.slice(0, RAIL_LABEL_MAX - 1)}…` : pretty;
}

// Renders one section's contents: the live email folder tree, a set of
// labeled subgroups (ADMIN), or a flat list. Shared by the desktop pane
// and the mobile drawer so the two can never drift apart.
function SectionBody({
  section,
  sectionId,
  pathname,
  pins,
  onTogglePin,
  collapsedSections,
  toggleSubgroup,
  sectionSlug,
}: {
  section: SidebarSection;
  sectionId: string;
  pathname: string;
  pins: SidebarItem[];
  onTogglePin: (item: SidebarItem) => void;
  collapsedSections: Set<string>;
  toggleSubgroup: (key: string) => void;
  sectionSlug: (heading: string) => string;
}) {
  // EMAIL's body is the live folder tree from /api/email/folders. It
  // renders its own <ul.sidebar-list>, plus the non-folder destinations
  // (Batch Email to Clients, Marketing Email Blast) above the tree —
  // anything left only in the section's `items` here would be built and
  // thrown away, which is how those two went invisible for months.
  if (section.key === "email") {
    return <EmailSidebarSection id={sectionId} collapsed={false} />;
  }

  if (section.subgroups) {
    return (
      <div className="sidebar-subgroups" id={sectionId}>
        {section.subgroups.map((sg) => {
          // Each subgroup folds independently (Grant 2026-06-07). Keyed
          // per parent so "ADMIN::People" can't collide with a section.
          const sgKey = `${section.heading ?? section.key}::${sg.heading}`;
          const sgClosed = collapsedSections.has(sgKey);
          return (
            <div className={`sidebar-subgroup${sgClosed ? " is-closed" : ""}`} key={sg.heading}>
              <div className="sidebar-subgroup-title">
                <button
                  type="button"
                  className="sidebar-subgroup-toggle"
                  aria-expanded={!sgClosed}
                  onClick={() => toggleSubgroup(sgKey)}
                  data-testid={`sidebar-subgroup-toggle-${sectionSlug(sg.heading)}`}
                >
                  <span>{sg.heading}</span>
                  <ChevronIcon />
                </button>
              </div>
              <ul className="sidebar-list">
                {sg.items.map((item) => (
                  <li key={item.to}>
                    <SidebarLink
                      item={item}
                      pathname={pathname}
                      collapsed={false}
                      pinned={isPinned(pins, item.to)}
                      onTogglePin={onTogglePin}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <ul className="sidebar-list" id={sectionId}>
      {section.items.map((item) => (
        <li key={item.to}>
          <SidebarLink
            item={item}
            pathname={pathname}
            collapsed={false}
            pinned={isPinned(pins, item.to)}
            onTogglePin={onTogglePin}
          />
        </li>
      ))}
    </ul>
  );
}

function ChevronIcon() {
  return (
    <svg
      className="sb-chev"
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function SidebarLink({
  item,
  pathname,
  collapsed,
  pinned,
  onTogglePin,
}: {
  item: SidebarItem;
  pathname: string;
  collapsed: boolean;
  pinned?: boolean;
  onTogglePin?: (item: SidebarItem) => void;
}) {
  const isActive = sidebarItemIsActive(pathname, item.to);
  return (
    <div className="sidebar-link-row">
      <Link
        to={item.to}
        className={`sidebar-link${isActive ? " is-active" : ""}`}
        aria-current={isActive ? "page" : undefined}
        // Always name the link for assistive tech. In collapsed icon-rail
        // mode the visible text label isn't rendered and the icon is
        // aria-hidden, so without this the link had no accessible name
        // (2026-07-23 audit P0-9). `title` alone is not a reliable name.
        aria-label={item.label}
        title={collapsed ? item.label : undefined}
        data-testid={`sidebar-link-${item.to}`}
      >
        <span className="sidebar-link-icon" aria-hidden="true">
          <SidebarIcon name={item.icon} />
        </span>
        {!collapsed && (
          <>
            <span className="sidebar-link-label">
              {item.label}
              {/* Inline "what is this?" help next to the label, for the
                  core-vocabulary items that carry a `help` key. Travels
                  with the word (not the far-right edge) so it never
                  collides with the badge / hover pin. */}
              {item.help && <CoreVocabularyTip k={item.help} />}
            </span>
            {item.preview && <span className="sidebar-link-preview">Preview</span>}
            {item.badge && (
              // Grant, 2026-09-11: this badge counts the VIEWER's items while
              // the page opens on everyone's, so the two numbers look like
              // they disagree. The count is right; say whose it is.
              <span className="sidebar-link-badge" title={`${item.badge} routed to you`}>
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
      {/* Pin/unpin affordance (PR 6) — shows on hover, filled when
          pinned. Hidden in the icon-rail collapsed mode (no room). */}
      {!collapsed && onTogglePin && (
        <button
          type="button"
          className={`sidebar-pin-btn${pinned ? " is-pinned" : ""}`}
          aria-label={pinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
          aria-pressed={pinned ?? false}
          title={pinned ? "Unpin" : "Pin to top"}
          onClick={() => onTogglePin(item)}
        >
          <PinStarIcon filled={!!pinned} />
        </button>
      )}
    </div>
  );
}

function PinStarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function PanelIcon({ collapsed }: { collapsed: boolean }) {
  // Lucide panel-left-open / panel-left-close — inline SVG so we don't
  // need to add the lucide-react package as a dependency for this PR.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      {collapsed ? <path d="M14 9l3 3-3 3" /> : <path d="M16 9l-3 3 3 3" />}
    </svg>
  );
}

// Rail and panel icons: the app's own lucide-react set (1.5px stroke, no
// fill, 18px), one component per IconName. The Record type makes a name
// added to IconName without an icon here (or the reverse) a type error,
// so no row can ever render a blank square. Replaced the hand-copied
// inline SVG subset 2026-10-15, when the navigation overhaul needed
// ~60 distinct icons instead of reusing "activity" for eight rows.
const SIDEBAR_ICONS: Record<IconName, LucideIcon> = {
  activity: Activity,
  archive: Archive,
  "badge-check": BadgeCheck,
  banknote: Banknote,
  "bar-chart": ChartColumn,
  bookmark: Bookmark,
  "book-open": BookOpen,
  briefcase: Briefcase,
  building: Building,
  "building-2": Building2,
  calculator: Calculator,
  calendar: Calendar,
  "calendar-check": CalendarCheck,
  "calendar-cog": CalendarCog,
  chat: MessageCircle,
  "check-circle": CircleCheck,
  "check-square": SquareCheckBig,
  "circle-dot": CircleDot,
  "clipboard-list": ClipboardList,
  clock: Clock,
  columns: Columns3,
  compass: Compass,
  cpu: Cpu,
  "data-hygiene": Database,
  draft: FilePen,
  eye: Eye,
  "file-chart": FileChartColumn,
  "file-text": FileText,
  folder: Folder,
  "folder-check": FolderCheck,
  gauge: Gauge,
  "graduation-cap": GraduationCap,
  "hand-coins": HandCoins,
  handshake: Handshake,
  "heart-handshake": HeartHandshake,
  home: House,
  "id-card": IdCard,
  inbox: Inbox,
  junk: ShieldAlert,
  kanban: SquareKanban,
  key: Key,
  landmark: Landmark,
  "layout-dashboard": LayoutDashboard,
  "layout-grid": LayoutGrid,
  "layout-list": LayoutList,
  library: Library,
  "life-buoy": LifeBuoy,
  "line-chart": ChartLine,
  link: Link2,
  list: List,
  "list-checks": ListChecks,
  "list-filter": ListFilter,
  lock: Lock,
  mail: Mail,
  "mail-check": MailCheck,
  "mail-open": MailOpen,
  mails: Mails,
  megaphone: Megaphone,
  "message-square": MessageSquare,
  mic: Mic,
  network: Network,
  "notebook-pen": NotebookPen,
  outbox: Upload,
  package: Package,
  pen: Pen,
  "pen-tool": PenTool,
  percent: Percent,
  plug: Plug,
  "radio-tower": RadioTower,
  receipt: Receipt,
  route: Route,
  "scan-search": ScanSearch,
  send: Send,
  settings: Settings,
  "settings-2": Settings2,
  share: Share2,
  shield: Shield,
  "shield-check": ShieldCheck,
  sliders: SlidersHorizontal,
  star: Star,
  sun: Sun,
  target: Target,
  team: Users,
  trash: Trash2,
  "trending-up": TrendingUp,
  type: Type,
  umbrella: Umbrella,
  upload: Upload,
  user: User,
  users: Users,
  wallet: Wallet,
  workflow: Workflow,
};

function SidebarIcon({ name }: { name: IconName }) {
  const Icon = SIDEBAR_ICONS[name];
  return <Icon size={18} strokeWidth={1.5} aria-hidden="true" />;
}

